/* =====================================================================
   Directkoop.nl — Custom theme behaviour
   - Scroll reveal (IntersectionObserver)
   - Hero parallax
   - Cart count animation
   - Header scroll state, mobile menu, expanding search
   - Product gallery, variant selection, AJAX add-to-cart
   - Accordion, collection sort
   Respects prefers-reduced-motion.
   ===================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var els = document.querySelectorAll('.reveal, .dk-trust');
    if (!els.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el, i) {
      el.style.setProperty('--reveal-delay', (i % 3) * 0.08 + 's');
      io.observe(el);
    });
  }

  /* ---------- Hero parallax ---------- */
  function initParallax() {
    var heroImg = document.querySelector('[data-parallax]');
    if (!heroImg || reduceMotion) return;
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          heroImg.style.transform = 'translateY(' + (window.scrollY * 0.25) + 'px)';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---------- Header scroll state ---------- */
  function initHeaderScroll() {
    var header = document.querySelector('.dk-header');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    var openBtn = document.querySelector('[data-menu-open]');
    var closeBtn = document.querySelector('[data-menu-close]');
    var menu = document.querySelector('.dk-mobile-menu');
    if (!menu) return;

    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('dk-no-scroll');
    }
    function close() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('dk-no-scroll');
    }
    if (openBtn) openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    menu.querySelectorAll('a').forEach(function (a) { a.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ---------- Expanding search ---------- */
  function initSearch() {
    var wrap = document.querySelector('.dk-search');
    var toggle = document.querySelector('[data-search-toggle]');
    if (!wrap || !toggle) return;
    var field = wrap.querySelector('.dk-search__field');
    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      var isOpen = wrap.classList.toggle('is-open');
      if (isOpen && field) { field.focus(); }
    });
    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target) && e.target !== toggle) wrap.classList.remove('is-open');
    });
  }

  /* ---------- Cart count animation + AJAX refresh ---------- */
  function bumpCart() {
    var cart = document.querySelector('.dk-cart');
    if (!cart || reduceMotion) return;
    cart.classList.remove('is-bumping');
    void cart.offsetWidth; // reflow to restart animation
    cart.classList.add('is-bumping');
  }

  function refreshCartCount() {
    return fetch(window.Shopify ? '/cart.js' : '/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var el = document.querySelector('.dk-cart__count');
        if (!el) return;
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
        bumpCart();
      })
      .catch(function () {});
  }

  /* ---------- Product gallery ---------- */
  function initGallery() {
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    var mains = gallery.querySelectorAll('[data-gallery-main] img');
    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var id = thumb.getAttribute('data-media-id');
        mains.forEach(function (img) { img.classList.toggle('is-active', img.getAttribute('data-media-id') === id); });
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
      });
    });
  }

  /* ---------- Variant selection + AJAX add to cart ---------- */
  function initProductForm() {
    var form = document.querySelector('[data-product-form]');
    if (!form) return;

    var variantsData = [];
    try { variantsData = JSON.parse(form.getAttribute('data-variants') || '[]'); } catch (e) {}

    var selected = {};
    var optionButtons = form.querySelectorAll('[data-option-index]');
    var idInput = form.querySelector('[name="id"]');
    var priceEl = document.querySelector('[data-product-price]');
    var stockEl = document.querySelector('[data-product-stock]');
    var atc = form.querySelector('.dk-atc');
    var atcText = form.querySelector('.dk-atc__text');

    // initialise selected from any pre-selected buttons
    optionButtons.forEach(function (btn) {
      if (btn.classList.contains('is-selected')) {
        selected[btn.getAttribute('data-option-index')] = btn.getAttribute('data-value');
      }
    });

    function findVariant() {
      return variantsData.find(function (v) {
        return v.options.every(function (opt, i) { return selected[i] === opt; });
      });
    }

    function formatMoney(cents) {
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }

    function updateVariant() {
      var variant = findVariant();
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      if (priceEl) {
        priceEl.innerHTML = variant.compare_at_price && variant.compare_at_price > variant.price
          ? '<del>' + formatMoney(variant.compare_at_price) + '</del>' + formatMoney(variant.price)
          : formatMoney(variant.price);
      }
      if (stockEl) {
        if (variant.available) {
          stockEl.classList.remove('dk-product__stock--out');
          stockEl.querySelector('[data-stock-label]').textContent = 'Op voorraad';
        } else {
          stockEl.classList.add('dk-product__stock--out');
          stockEl.querySelector('[data-stock-label]').textContent = 'Uitverkocht';
        }
      }
      if (atc) {
        atc.disabled = !variant.available;
        if (atcText) atcText.textContent = variant.available ? 'In winkelwagen' : 'Uitverkocht';
      }
      // Toon de foto van de gekozen variant (bv. kleur) in de galerij
      var mediaId = null;
      if (variant.featured_media && variant.featured_media.id != null) {
        mediaId = String(variant.featured_media.id);
      } else if (variant.featured_image && variant.featured_image.id != null) {
        mediaId = String(variant.featured_image.id);
      }
      if (mediaId) activateGalleryMedia(mediaId);
    }

    function activateGalleryMedia(mediaId) {
      var mains = document.querySelectorAll('[data-gallery-main] img');
      var thumbs = document.querySelectorAll('[data-gallery-thumb]');
      var matched = false;
      mains.forEach(function (img) {
        var on = img.getAttribute('data-media-id') === mediaId;
        if (on) matched = true;
        img.classList.toggle('is-active', on);
      });
      if (!matched) return; // geen bijpassende foto → galerij ongemoeid laten
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-media-id') === mediaId);
      });
    }

    optionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-unavailable')) return;
        var index = btn.getAttribute('data-option-index');
        selected[index] = btn.getAttribute('data-value');
        form.querySelectorAll('[data-option-index="' + index + '"]').forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        updateVariant();
      });
    });

    updateVariant();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (atc.disabled) return;
      atc.classList.add('is-loading');
      var formData = new FormData(form);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          return refreshCartCount();
        })
        .then(function () {
          if (atcText) atcText.textContent = 'Toegevoegd ✓';
          setTimeout(function () { if (atcText) atcText.textContent = 'In winkelwagen'; }, 1600);
        })
        .catch(function () {
          if (atcText) atcText.textContent = 'Probeer opnieuw';
        })
        .finally(function () {
          atc.classList.remove('is-loading');
        });
    });
  }

  /* ---------- Quick add (homepage cards) ---------- */
  function initQuickAdd() {
    document.querySelectorAll('[data-quick-add]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;
        btn.textContent = '...';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function () { return refreshCartCount(); })
          .then(function () {
            btn.textContent = 'Toegevoegd ✓';
            setTimeout(function () { btn.textContent = 'Snel toevoegen'; }, 1600);
          })
          .catch(function () { btn.textContent = 'Snel toevoegen'; });
      });
    });
  }

  /* ---------- Accordion ---------- */
  function initAccordion() {
    document.querySelectorAll('.dk-accordion__trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        var panel = trigger.nextElementSibling;
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (panel) {
          panel.style.maxHeight = expanded ? '0px' : panel.scrollHeight + 'px';
        }
      });
    });
  }

  /* ---------- Collection sort (pill buttons) ---------- */
  function initCollectionSort() {
    var bar = document.querySelector('[data-filterbar]');
    if (!bar) return;
    bar.querySelectorAll('[data-sort]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var grid = document.querySelector('[data-collection-grid]');
        if (grid) grid.classList.add('is-loading');
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', btn.getAttribute('data-sort'));
        window.location.href = url.toString();
      });
    });
  }

  /* ---------- Cart page (quantity + remove) ---------- */
  function initCart() {
    var form = document.querySelector('[data-cart-form]');
    if (!form) return;

    function changeLine(line, quantity, itemEl) {
      if (itemEl) itemEl.classList.add('is-removing');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function () { window.location.reload(); })
        .catch(function () { if (itemEl) itemEl.classList.remove('is-removing'); });
    }

    form.querySelectorAll('[data-cart-item]').forEach(function (itemEl) {
      var line = parseInt(itemEl.getAttribute('data-line'), 10);
      var input = itemEl.querySelector('[data-qty-input]');

      var down = itemEl.querySelector('[data-qty-down]');
      var up = itemEl.querySelector('[data-qty-up]');
      var remove = itemEl.querySelector('[data-remove]');

      if (down) down.addEventListener('click', function () {
        var q = Math.max(0, parseInt(input.value, 10) - 1);
        changeLine(line, q, itemEl);
      });
      if (up) up.addEventListener('click', function () {
        var q = parseInt(input.value, 10) + 1;
        changeLine(line, q, itemEl);
      });
      if (input) input.addEventListener('change', function () {
        var q = Math.max(0, parseInt(input.value, 10) || 0);
        changeLine(line, q, itemEl);
      });
      if (remove) remove.addEventListener('click', function () {
        changeLine(line, 0, itemEl);
      });
    });
  }

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initParallax();
    initHeaderScroll();
    initMobileMenu();
    initSearch();
    initGallery();
    initProductForm();
    initQuickAdd();
    initAccordion();
    initCollectionSort();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // expose for theme editor / other scripts
  window.Directkoop = { refreshCartCount: refreshCartCount, bumpCart: bumpCart };
})();
